import { useEffect, useRef, useState } from "react";
import { Loader2, Search, X } from "lucide-react";

/**
 * Buscador/autocomplete reutilizable y centralizado del CRM.
 *
 * UX estándar: escribir → coincidencias debajo → refinar → seleccionar.
 * - Debounce + cancelación de peticiones obsoletas (la última gana).
 * - Navegación con teclado (↑/↓, Enter, Escape), click fuera para cerrar.
 * - Estados: loading, sin resultados, selección.
 *
 * La normalización y la prioridad de resultados viven en el `fetcher`
 * (normalmente un endpoint del backend con `search_norm` + ranking), de modo
 * que cada módulo comparte esta MISMA experiencia aunque busque por campos
 * distintos.
 *
 * @param {(q:string)=>Promise<any[]>} fetcher  Devuelve los resultados ya ordenados por relevancia.
 * @param {(item:any)=>void} onSelect
 * @param {(item:any, state:{active:boolean})=>React.ReactNode} renderItem
 */
export function SearchAutocomplete({
  fetcher,
  onSelect,
  renderItem,
  getKey = (it) => it.id,
  placeholder = "Buscar…",
  debounceMs = 250,
  minChars = 1,
  maxResults = 10,
  testid = "search-autocomplete",
  className = "",
  inputClassName = "",
  autoFocus = false,
  emptyLabel = "Sin resultados",
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [touched, setTouched] = useState(false);
  const [hi, setHi] = useState(-1);

  const rootRef = useRef(null);
  const listRef = useRef(null);
  const reqIdRef = useRef(0);
  const fetcherRef = useRef(fetcher);
  useEffect(() => { fetcherRef.current = fetcher; }, [fetcher]);

  // Debounce + cancelación de respuestas obsoletas.
  useEffect(() => {
    const term = q.trim();
    if (term.length < minChars) {
      setResults([]); setLoading(false); setTouched(false); setHi(-1);
      return undefined;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      const myId = ++reqIdRef.current;
      try {
        const data = await fetcherRef.current(term);
        if (myId !== reqIdRef.current) return;
        const list = Array.isArray(data) ? data.slice(0, maxResults) : [];
        setResults(list); setHi(list.length ? 0 : -1);
      } catch {
        if (myId !== reqIdRef.current) return;
        setResults([]); setHi(-1);
      } finally {
        if (myId === reqIdRef.current) { setLoading(false); setTouched(true); }
      }
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [q, minChars, maxResults, debounceMs]);

  // Click fuera → cerrar.
  useEffect(() => {
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Mantener visible el elemento resaltado.
  useEffect(() => {
    if (hi < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-index="${hi}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [hi]);

  const choose = (it) => {
    if (!it) return;
    onSelect(it);
    setOpen(false); setQ(""); setResults([]); setTouched(false); setHi(-1);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault(); setOpen(true);
      setHi((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (open && results[hi]) { e.preventDefault(); choose(results[hi]); }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showDrop = open && q.trim().length >= minChars;
  const defaultRender = (it) => <span className="truncate">{it.label ?? it.name ?? it.code ?? String(getKey(it))}</span>;
  const render = renderItem || defaultRender;

  return (
    <div ref={rootRef} className={`relative ${className}`} data-testid={testid}>
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A9098] pointer-events-none" />
        <input
          type="text"
          role="combobox"
          aria-expanded={showDrop}
          aria-autocomplete="list"
          autoFocus={autoFocus}
          value={q}
          placeholder={placeholder}
          data-testid={`${testid}-input`}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          className={`w-full pl-9 pr-8 py-2 text-sm bg-white border border-[#D5D7CE] focus:border-[#6E8B3D] focus:outline-none focus:ring-1 focus:ring-[#6E8B3D] transition-colors ${inputClassName}`}
        />
        {loading ? (
          <Loader2 size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A9098] animate-spin" />
        ) : q ? (
          <button type="button" aria-label="Limpiar" data-testid={`${testid}-clear`}
            onClick={() => { setQ(""); setResults([]); setTouched(false); setHi(-1); }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#8A9098] hover:text-[#111110]">
            <X size={14} />
          </button>
        ) : null}
      </div>

      {showDrop && (
        <div ref={listRef} role="listbox" data-testid={`${testid}-results`}
          className="absolute z-50 mt-1 w-full max-h-80 overflow-y-auto bg-white border border-[#D5D7CE] shadow-lg">
          {results.length > 0 ? (
            results.map((it, i) => (
              <button
                key={getKey(it)}
                type="button"
                role="option"
                aria-selected={i === hi}
                data-index={i}
                data-testid={`${testid}-option-${i}`}
                onMouseEnter={() => setHi(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(it)}
                className={`w-full text-left px-3 py-2 text-sm border-b border-[#F0F0EC] last:border-b-0 ${i === hi ? "bg-[#EFF3E6]" : "bg-white hover:bg-[#F7F7F4]"}`}>
                {render(it, { active: i === hi })}
              </button>
            ))
          ) : loading ? (
            <div className="px-3 py-3 text-sm text-[#8A9098]" data-testid={`${testid}-loading`}>Buscando…</div>
          ) : touched ? (
            <div className="px-3 py-3 text-sm text-[#8A9098]" data-testid={`${testid}-empty`}>{emptyLabel}</div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default SearchAutocomplete;
